/**
 * <AccountTimezonePage> — l'écran « Fuseau horaire » d'un compte (page).
 *
 * En haut, le fuseau en cours (« Oslo », « Europe/Oslo ») et l'interrupteur « Fuseau automatique »
 * avec sa phrase. Coupé, la page ouvre dessous la liste où l'on choisit son fuseau
 * (`<TimezonePicker>`). Le réglage, sur le modèle de Slack (« Set time zone automatically ») et de
 * Google Agenda (« Use device time zone »).
 *
 * Composition pure : tout arrive en props, l'interrupteur et le choix remontent par rappels. Pas de
 * chargement de page — le compte est déjà là ; seule la liste charge, et montre sa forme.
 */
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Banner } from "../../atoms/Banner"
import { Divider } from "../../atoms/Divider"
import { Stack } from "../../atoms/containers/Stack"
import { Switch } from "../../atoms/Switch"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { Header } from "../../molecules/Header"
import { TimezonePicker } from "../../organisms/TimezonePicker"
import { PageTemplate } from "../../templates/PageTemplate"
import { type UITimezoneOption } from "../../../ports/primary/ui/types"

/**
 * Une colonne de réglage, pas un tableau de bord : au-delà, une ligne de la liste s'étirerait entre
 * la ville, à gauche, et sa région, à l'autre bout de l'écran.
 */
const SETTINGS_CONTENT_WIDTH = 640

export type AccountTimezonePageProps = {
    /** « Oslo » — absent quand le compte n'a pas encore de fuseau. */
    currentCity?: string | null
    /** « Europe/Oslo » — le libellé entier, en français quand il est connu. */
    currentLabel?: string | null
    auto: boolean
    /** La phrase sous l'interrupteur — ce que suit le fuseau automatique. */
    autoHint: string
    /** L'interrupteur attend la réponse : il ne se rebascule pas entre-temps. */
    autoSaving?: boolean
    onToggleAuto: (next: boolean) => void
    /** Le refus d'une écriture, déjà écrit. */
    errorMessage?: string | null

    // ── La liste, en manuel seulement
    options: UITimezoneOption[]
    optionsLoading?: boolean
    optionsError?: string | null
    onRetryOptions?: () => void
    selectedTimezoneId?: string | null
    savingTimezoneId?: string | null
    onSelectTimezone: (id: string) => void

    onClose?: () => void
    testID?: string
}

export function AccountTimezonePage(props: AccountTimezonePageProps) {
    const {
        currentCity,
        currentLabel,
        auto,
        autoHint,
        autoSaving = false,
        onToggleAuto,
        errorMessage,
        options,
        optionsLoading,
        optionsError,
        onRetryOptions,
        selectedTimezoneId,
        savingTimezoneId,
        onSelectTimezone,
        onClose,
        testID,
    } = props

    const header = (
        <Header
            title="Fuseau horaire"
            left={
                onClose != null ? (
                    <IconButton
                        iconName="chevron-left"
                        appearance="transparent"
                        iconColor="primary"
                        onPress={onClose}
                    />
                ) : undefined
            }
        />
    )

    return (
        <PageTemplate
            testID={testID}
            header={header}
            padding="4"
            maxContentWidth={SETTINGS_CONTENT_WIDTH}
            // On touche une ligne de la liste sans perdre le clavier de la recherche.
            keyboardShouldPersistTaps="handled"
        >
            <Stack gap="4" style={styles.root}>
                <Card p="4">
                    <Stack gap="3">
                        <SectionLabel>Fuseau en cours</SectionLabel>
                        {currentCity != null && currentCity !== "" ? (
                            <Stack gap="1">
                                <Text size="text-lg" weight="bold" color="text-strong">
                                    {currentCity}
                                </Text>
                                {currentLabel != null && currentLabel !== currentCity && (
                                    <Text size="text-sm" color="text-muted">
                                        {currentLabel}
                                    </Text>
                                )}
                            </Stack>
                        ) : (
                            <Text size="text-md" color="text-muted">
                                Aucun fuseau pour l'instant.
                            </Text>
                        )}

                        <Divider spacing="1" />

                        <Switch
                            checked={auto}
                            onChange={onToggleAuto}
                            label="Fuseau automatique"
                            disabled={autoSaving}
                            testID="timezone-auto"
                        />
                        <Text size="text-sm" color="text-muted">
                            {autoHint}
                        </Text>

                        {errorMessage != null && errorMessage !== "" && (
                            <Banner
                                message={errorMessage}
                                color="danger"
                                icon="exclamation-circle"
                                align="start"
                                tinted
                            />
                        )}
                    </Stack>
                </Card>

                {!auto && (
                    <Stack gap="2">
                        <SectionLabel>Choisir un fuseau</SectionLabel>
                        <TimezonePicker
                            options={options}
                            selectedId={selectedTimezoneId}
                            savingId={savingTimezoneId}
                            onSelect={onSelectTimezone}
                            loading={optionsLoading}
                            error={optionsError}
                            onRetry={onRetryOptions}
                        />
                    </Stack>
                )}
            </Stack>
        </PageTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    root: { width: "100%" },
}))
